import React, { useRef, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const COLOURS = [
  { hex: '#1a1a1a', label: 'Black' },
  { hex: '#e05c7a', label: 'Pink' },
  { hex: '#3b82f6', label: 'Blue' },
  { hex: '#22c55e', label: 'Green' },
  { hex: '#b57bee', label: 'Purple' },
  { hex: '#f59e0b', label: 'Orange' },
  { hex: '#ef4444', label: 'Red' },
  { hex: '#ffffff', label: 'White' },
];

export default function Whiteboard({ sessionId, role, isLocked }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPos = useRef(null);
  const [colour, setColour] = useState(role === 'tutor' ? '#b57bee' : '#1a1a1a');
  const [penSize, setPenSize] = useState(4);
  const [eraserSize, setEraserSize] = useState(30);
  const [tool, setTool] = useState('pen');
  const [opacity, setOpacity] = useState(1);
  const strokeBuffer = useRef([]);
  const flushTimer = useRef(null);
  const pendingStrokes = useRef([]);
  const strokeHistory = useRef([]); // [{id, strokes:[]}] per draw gesture for undo

  const getCtx = () => canvasRef.current?.getContext('2d');

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top) * scaleY,
    };
  };

  const drawStroke = useCallback((ctx, stroke) => {
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(stroke.x0, stroke.y0);
    ctx.lineTo(stroke.x1, stroke.y1);
    if (stroke.tool === 'eraser') {
      ctx.globalCompositeOperation = 'destination-out';
      ctx.strokeStyle = 'rgba(0,0,0,1)';
      ctx.lineWidth = stroke.size;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    } else if (stroke.tool === 'highlighter') {
      // Use butt caps so segments join flush with no overlapping circles
      ctx.globalCompositeOperation = 'source-over';
      // Parse colour and apply fixed opacity directly in the colour
      ctx.strokeStyle = stroke.colour;
      ctx.globalAlpha = 0.28;
      ctx.lineWidth = stroke.size;
      ctx.lineCap = 'butt';
      ctx.lineJoin = 'round';
    } else {
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = stroke.colour;
      ctx.globalAlpha = stroke.opacity || 1;
      ctx.lineWidth = stroke.size;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
    ctx.stroke();
    ctx.restore();
  }, []);

  const flushStrokes = useCallback(async () => {
    const strokes = [...strokeBuffer.current];
    if (!strokes.length) return;
    strokeBuffer.current = [];
    await supabase.from('whiteboard_strokes').insert(strokes.map(s => ({
      session_id: sessionId,
      x0: s.x0, y0: s.y0, x1: s.x1, y1: s.y1,
      colour: s.colour, size: s.size, tool: s.tool,
    })));
  }, [sessionId]);

  useEffect(() => {
    const canvas = canvasRef.current;
    canvas.width = 1600;
    canvas.height = 900;
    const ctx = getCtx();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    supabase.from('whiteboard_strokes').select('*').eq('session_id', sessionId).order('created_at')
      .then(({ data }) => { if (data) data.forEach(s => drawStroke(ctx, s)); });

    const channel = supabase.channel(`wb-${sessionId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'whiteboard_strokes', filter: `session_id=eq.${sessionId}` },
        (payload) => {
          const s = payload.new;
          const already = pendingStrokes.current.some(p =>
            Math.abs(p.x0 - s.x0) < 0.5 && Math.abs(p.y0 - s.y0) < 0.5 && Math.abs(p.x1 - s.x1) < 0.5
          );
          if (!already) drawStroke(getCtx(), s);
        })
      .subscribe();

    const evChannel = supabase.channel(`wb-ev-${sessionId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'whiteboard_events', filter: `session_id=eq.${sessionId}` },
        (payload) => {
          if (payload.new.event_type === 'clear') {
            const c = getCtx();
            c.fillStyle = '#ffffff';
            c.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          }
        })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(evChannel);
      if (flushTimer.current) clearTimeout(flushTimer.current);
    };
  }, [sessionId, drawStroke]);

  const canDraw = role === 'tutor' || !isLocked;
  const activeSize = tool === 'eraser' ? eraserSize : penSize;

  const currentStrokeGroup = useRef([]);

  const startDraw = (e) => {
    if (!canDraw) return;
    e.preventDefault();
    drawing.current = true;
    lastPos.current = getPos(e);
    currentStrokeGroup.current = [];
  };

  const doDraw = (e) => {
    if (!drawing.current || !canDraw) return;
    e.preventDefault();
    const pos = getPos(e);
    const stroke = { x0: lastPos.current.x, y0: lastPos.current.y, x1: pos.x, y1: pos.y, colour, size: activeSize, tool, opacity };
    drawStroke(getCtx(), stroke);
    strokeBuffer.current.push(stroke);
    currentStrokeGroup.current.push(stroke);
    pendingStrokes.current.push(stroke);
    if (pendingStrokes.current.length > 100) pendingStrokes.current.shift();
    lastPos.current = pos;
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flushStrokes, 80);
  };

  const stopDraw = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (currentStrokeGroup.current.length > 0) {
      strokeHistory.current.push([...currentStrokeGroup.current]);
      currentStrokeGroup.current = [];
    }
    flushStrokes();
  };

  const undoLast = async () => {
    if (role !== 'tutor' || strokeHistory.current.length === 0) return;
    strokeHistory.current.pop();
    // Redraw everything from history
    const ctx = getCtx();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    // Clear DB and re-insert remaining
    await supabase.from('whiteboard_strokes').delete().eq('session_id', sessionId);
    const remaining = strokeHistory.current.flat();
    if (remaining.length > 0) {
      await supabase.from('whiteboard_strokes').insert(remaining.map(s => ({
        session_id: sessionId, x0: s.x0, y0: s.y0, x1: s.x1, y1: s.y1,
        colour: s.colour, size: s.size, tool: s.tool,
      })));
      remaining.forEach(s => drawStroke(ctx, s));
    }
    await supabase.from('whiteboard_events').insert([{ session_id: sessionId, event_type: 'clear' }]);
    // Brief delay then redraw for others
    setTimeout(async () => {
      const { data } = await supabase.from('whiteboard_strokes').select('*').eq('session_id', sessionId).order('created_at');
      const c = getCtx();
      c.fillStyle = '#ffffff';
      c.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      if (data) data.forEach(s => drawStroke(c, s));
    }, 300);
  };

  const clearCanvas = async () => {
    if (role !== 'tutor') return;
    const ctx = getCtx();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    await supabase.from('whiteboard_strokes').delete().eq('session_id', sessionId);
    await supabase.from('whiteboard_events').insert([{ session_id: sessionId, event_type: 'clear' }]);
  };

  const toolColour = { pen: '#20396B', highlighter: '#FFC84B', eraser: '#94a3b8' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 8 }}>

      {/* ── Goodnotes-style toolbar ── */}
      <div style={{
        background: '#fff', borderRadius: 14, padding: '10px 14px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)', display: 'flex',
        alignItems: 'center', gap: 14, flexWrap: 'wrap',
      }}>

        {/* Tool selector */}
        <div style={{ display: 'flex', gap: 4, background: '#f5f3ff', borderRadius: 10, padding: 4 }}>
          {[
            { key: 'pen', icon: '✏️', label: 'Pen' },
            { key: 'highlighter', icon: '🖊️', label: 'Highlight' },
            { key: 'eraser', icon: '⬜', label: 'Eraser' },
          ].map(t => (
            <button key={t.key} onClick={() => setTool(t.key)} style={{
              padding: '6px 12px', borderRadius: 8, border: 'none',
              background: tool === t.key ? toolColour[t.key] : 'transparent',
              color: tool === t.key ? '#fff' : '#888',
              fontSize: 13, fontWeight: tool === t.key ? 'bold' : 'normal',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
              transition: 'all 0.15s',
            }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <div style={{ width: 1, height: 32, background: '#e8e0f5' }} />

        {/* Colour swatches — hidden when eraser active */}
        {tool !== 'eraser' && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {COLOURS.map(c => (
              <button key={c.hex} title={c.label} onClick={() => setColour(c.hex)} style={{
                width: 26, height: 26, borderRadius: '50%', background: c.hex,
                border: colour === c.hex ? '3px solid #555' : c.hex === '#ffffff' ? '2px solid #ccc' : '2px solid transparent',
                cursor: 'pointer', flexShrink: 0, padding: 0,
                boxShadow: colour === c.hex ? '0 0 0 2px #b57bee' : 'none',
                transition: 'all 0.12s',
              }} />
            ))}
          </div>
        )}

        {tool !== 'eraser' && <div style={{ width: 1, height: 32, background: '#e8e0f5' }} />}

        {/* Size slider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 180 }}>
          <span style={{ fontSize: 12, color: '#aaa', whiteSpace: 'nowrap' }}>
            {tool === 'eraser' ? 'Eraser size' : 'Pen size'}
          </span>
          <input
            type="range"
            min={tool === 'eraser' ? 10 : 1}
            max={tool === 'eraser' ? 120 : 40}
            value={tool === 'eraser' ? eraserSize : penSize}
            onChange={e => tool === 'eraser' ? setEraserSize(Number(e.target.value)) : setPenSize(Number(e.target.value))}
            style={{ flex: 1 }}
          />
          {/* Live preview */}
          <div style={{
            width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <div style={{
              width: Math.min(activeSize * (tool === 'eraser' ? 0.28 : 0.8), 30),
              height: Math.min(activeSize * (tool === 'eraser' ? 0.28 : 0.8), 30),
              borderRadius: '50%',
              background: tool === 'eraser' ? '#e0e0e0' : colour,
              border: tool === 'eraser' ? '2px solid #bbb' : 'none',
              opacity: tool === 'highlighter' ? 0.45 : 1,
            }} />
          </div>
        </div>

        {/* Opacity slider — pen and highlighter only */}
        {tool !== 'eraser' && (
          <>
            <div style={{ width: 1, height: 32, background: '#e8e0f5' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 140 }}>
              <span style={{ fontSize: 12, color: '#aaa', whiteSpace: 'nowrap' }}>Opacity</span>
              <input
                type="range" min={10} max={100} value={Math.round(opacity * 100)}
                onChange={e => setOpacity(Number(e.target.value) / 100)}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: 12, color: '#888', minWidth: 30 }}>{Math.round(opacity * 100)}%</span>
            </div>
          </>
        )}

        {role === 'tutor' && (
          <>
            <div style={{ width: 1, height: 32, background: '#e8e0f5' }} />
            <button onClick={undoLast} style={{
              padding: '6px 14px', borderRadius: 10, background: '#C8E6FA',
              border: '2px solid #90caf9', cursor: 'pointer', fontSize: 13,
              color: '#1565c0', fontWeight: 'bold',
            }}>
              ↩️ Undo
            </button>
            <button onClick={clearCanvas} style={{
              padding: '6px 14px', borderRadius: 10, background: '#FFD6E0',
              border: '2px solid #f48fb1', cursor: 'pointer', fontSize: 13,
              color: '#c04060', fontWeight: 'bold',
            }}>
              🗑️ Clear
            </button>
          </>
        )}
      </div>

      {/* Canvas */}
      <div style={{
        flex: 1, position: 'relative', background: '#fff', borderRadius: 12,
        overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.08)', border: '2px solid #f0e8ff',
      }}>
        {!canDraw && (
          <div style={{
            position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)',
            background: '#FFD6E0', borderRadius: 10, padding: '6px 16px',
            fontSize: 13, color: '#c04060', fontWeight: 'bold', zIndex: 10,
          }}>
            🔒 Drawing is paused — listen to your tutor!
          </div>
        )}
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: '100%', display: 'block', touchAction: 'none', cursor: canDraw ? (tool === 'eraser' ? 'cell' : 'crosshair') : 'not-allowed' }}
          onMouseDown={startDraw} onMouseMove={doDraw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
          onTouchStart={startDraw} onTouchMove={doDraw} onTouchEnd={stopDraw}
        />
      </div>
    </div>
  );
}
